import type { BoardDoc } from '../doc/BoardDoc';
import { newId } from '../doc/BoardDoc';
import { distance, rotatedBounds, unionRects, type Point, type Rect } from '../doc/geometry';
import {
  DEFAULT_KANBAN_SIZE,
  DEFAULT_LINK_SIZE,
  DEFAULT_TABLE_SIZE,
  DEFAULT_TODO_SIZE,
  DEFAULT_RICHTEXT_SIZE,
  DEFAULT_SHAPE_SIZE,
  DEFAULT_TITLE_SIZE,
  NOTE_COLORS,
  type Geometry,
  type Item,
  type ItemPatch,
  type NewItem,
} from '../doc/schema';
import { api } from '../sync/api';
import type { PaneStore } from './paneStore';
import { withFrameContents } from './frames';
import { canSelect } from './ink';
import { boardNoteLook, notePropsFor } from './noteLook';
import { toolSettings } from './toolSettings';
import { loadRich, richSync } from './text/richLoader';
import { hasStoredClipboard, readStoredClipboard, rememberClipboard, snapshotForClipboard, type ClipboardPayload } from './clipboard';
import { cloneAssetLocally, storeImageAsset, type LocalAssetMeta } from './assets/localAssets';
import { enqueueUpload } from '../sync/uploadQueue';
import { RICH_TYPES } from '../doc/schema';
import { centerOn, fitRect, rotateAt, screenToWorld, visibleWorldRect, zoomAt, type Camera } from './viewport/camera';
import { copyConnectorProps } from '../doc/connector';

export interface Ctx {
  board: BoardDoc;
  pane: PaneStore;
}

// ---- selection helpers ------------------------------------------------------

export function selectedItems({ board, pane }: Ctx): Item[] {
  const out: Item[] = [];
  for (const id of pane.getState().selection) {
    const item = board.index.get(id);
    if (item) out.push(item);
  }
  return out;
}

export function selectionBounds(ctx: Ctx): Rect | null {
  const { transient } = ctx.pane.getState();
  return unionRects(selectedItems(ctx).map((i) => rotatedBounds(withTransient(i, transient[i.id]))));
}

export function withTransient<T extends Geometry>(item: T, t: Partial<Geometry> | undefined): T {
  return t ? { ...item, ...t } : item;
}

/** Selects everything the current tool can select: strokes with the draw tool, everything else otherwise. */
export function selectAll({ board, pane }: Ctx): void {
  const { tool, select } = pane.getState();
  select(board.index.all().filter((i) => canSelect(i, tool) && !board.isHidden(i) && !board.isLocked(i)).map((i) => i.id));
}

export function deleteSelection({ board, pane }: Ctx): void {
  const { selection, clearSelection } = pane.getState();
  if (!selection.size) return;
  const ids = [...selection].filter((id) => {
    const item = board.index.get(id);
    return item && !board.isLocked(item);
  });
  if (ids.length) board.deleteItems(ids);
  clearSelection();
}

/** Writes the in-progress transient geometry to the document in one undoable transaction. */
export function commitTransient({ board, pane }: Ctx): void {
  const { transient, clearTransient } = pane.getState();
  const patches: [string, ItemPatch][] = [];
  for (const [id, g] of Object.entries(transient)) {
    const patch: ItemPatch = {};
    if (g.x !== undefined) patch.x = round(g.x);
    if (g.y !== undefined) patch.y = round(g.y);
    if (g.w !== undefined) patch.w = round(g.w);
    if (g.h !== undefined) patch.h = round(g.h);
    if (g.rotation !== undefined) patch.rotation = g.rotation;
    patches.push([id, patch]);
  }
  if (patches.length) board.updateItems(patches);
  clearTransient();
}

const round = (v: number) => Math.round(v * 100) / 100;

export function nudgeSelection(ctx: Ctx, dx: number, dy: number): void {
  const items = withFrameContents(ctx.board, selectedItems(ctx).filter((i) => !ctx.board.isLocked(i)));
  const patches: [string, ItemPatch][] = items.map((item) => [item.id, { x: item.x + dx, y: item.y + dy }]);
  if (patches.length) ctx.board.updateItems(patches);
}

// ---- creating items ---------------------------------------------------------

/** A sticky note in the board's default look (see noteLook.ts), centred on a point. */
export function createNoteAt(ctx: Ctx, world: Point, edit = true, text = ''): string {
  const look = boardNoteLook(ctx.board);
  const id = ctx.board.createItem({
    type: 'note',
    x: world.x - look.w / 2,
    y: world.y - look.h / 2,
    w: look.w,
    h: look.h,
    props: notePropsFor(look, text),
  });
  const s = ctx.pane.getState();
  s.select([id]);
  if (edit) s.setEditing(id);
  return id;
}

export function createTitleAt(ctx: Ctx, world: Point, edit = true): string {
  const { w, h } = DEFAULT_TITLE_SIZE;
  const id = ctx.board.createItem({
    type: 'title',
    x: world.x - w / 2,
    y: world.y - h / 2,
    w,
    h,
    props: { text: '', fontSize: 40, bold: true, align: 'left' },
  });
  const s = ctx.pane.getState();
  s.select([id]);
  if (edit) s.setEditing(id);
  return id;
}

export function createRichTextAt(ctx: Ctx, world: Point, edit = true): string {
  const { w, h } = DEFAULT_RICHTEXT_SIZE;
  const id = ctx.board.createItem({
    type: 'richtext',
    x: world.x - w / 2,
    y: world.y - h / 2,
    w,
    h,
    props: { bg: null, fontSize: 16 },
  });
  const s = ctx.pane.getState();
  s.select([id]);
  if (edit) s.setEditing(id);
  return id;
}

export async function createTableAt(ctx: Ctx, world: Point, edit = true): Promise<string> {
  await loadRich();
  const { w, h } = DEFAULT_TABLE_SIZE;
  const id = ctx.board.createItem({
    type: 'table',
    x: world.x - w / 2,
    y: world.y - h / 2,
    w,
    h,
    props: { bg: null, fontSize: 14, contentJson: (await loadRich()).tableJson(3, 3) },
  });
  const s = ctx.pane.getState();
  s.select([id]);
  if (edit) s.setEditing(id);
  return id;
}

export async function createTodoAt(ctx: Ctx, world: Point, edit = true): Promise<string> {
  await loadRich();
  const { w, h } = DEFAULT_TODO_SIZE;
  const id = ctx.board.createItem({
    type: 'todo',
    x: world.x - w / 2,
    y: world.y - h / 2,
    w,
    h,
    props: { bg: null, fontSize: 15, contentJson: (await loadRich()).taskListJson(['']) },
  });
  const s = ctx.pane.getState();
  s.select([id]);
  if (edit) s.setEditing(id);
  return id;
}

export function createKanbanAt(ctx: Ctx, world: Point, edit = true): string {
  const { w, h } = DEFAULT_KANBAN_SIZE;
  const id = ctx.board.createItem({
    type: 'kanban',
    x: world.x - w / 2,
    y: world.y - h / 2,
    w,
    h,
    props: {
      columns: [
        { id: newId(), title: 'To do', cards: [] },
        { id: newId(), title: 'Doing', cards: [] },
        { id: newId(), title: 'Done', cards: [] },
      ],
    },
  });
  const s = ctx.pane.getState();
  s.select([id]);
  if (edit) s.setEditing(id);
  return id;
}

/** Creates a shape from the current tool settings, either in a dragged rect or centred on a point. */
export function createShapeAt(ctx: Ctx, where: Rect | Point, opts: { flip?: boolean } = {}): string {
  const t = toolSettings.getState();
  const rect: Rect =
    'w' in where
      ? where
      : {
          x: where.x - DEFAULT_SHAPE_SIZE / 2,
          y: where.y - DEFAULT_SHAPE_SIZE / 2,
          w: DEFAULT_SHAPE_SIZE,
          h: DEFAULT_SHAPE_SIZE,
        };
  const isLine = t.shapeKind === 'line' || t.shapeKind === 'arrow';
  const id = ctx.board.createItem({
    type: 'shape',
    x: round(rect.x),
    y: round(rect.y),
    w: round(Math.max(1, rect.w)),
    h: round(Math.max(isLine ? 0 : 1, rect.h)),
    props: {
      kind: t.shapeKind,
      fill: isLine ? 'none' : t.fill,
      stroke: t.stroke === 'none' && (isLine || t.fill === 'none') ? '#111111' : t.stroke,
      strokeWidth: t.strokeWidth,
      flip: opts.flip ?? false,
    },
  });
  ctx.pane.getState().select([id]);
  return id;
}

const DEFAULT_FRAME_W = 800;
const DEFAULT_FRAME_H = 600;

/** A frame at a point (default size, centred) or filling a dragged rect. Frames start at the bottom. */
export function createFrameAt(ctx: Ctx, where: Rect | Point): string {
  const rect: Rect =
    'w' in where
      ? where
      : { x: where.x - DEFAULT_FRAME_W / 2, y: where.y - DEFAULT_FRAME_H / 2, w: DEFAULT_FRAME_W, h: DEFAULT_FRAME_H };
  const count = ctx.board.index.all().filter((i) => i.type === 'frame').length;
  const id = ctx.board.createItem({
    type: 'frame',
    x: round(rect.x),
    y: round(rect.y),
    w: round(Math.max(40, rect.w)),
    h: round(Math.max(40, rect.h)),
    props: { title: `Frame ${count + 1}`, color: null },
  });
  ctx.pane.getState().select([id]);
  return id;
}

const URL_RE = /^https?:\/\/[^\s]+$/i;
export const looksLikeUrl = (text: string): boolean => URL_RE.test(text.trim());

/** Creates a link card and fills it in from the server's preview endpoint. */
export function createLinkAt(ctx: Ctx, url: string, world: Point): string {
  const { w, h } = DEFAULT_LINK_SIZE;
  url = url.trim();
  const id = ctx.board.createItem({
    type: 'link',
    x: world.x - w / 2,
    y: world.y - h / 2,
    w,
    h,
    props: { url, title: '', description: '', image: null, site: '', status: 'loading' },
  });
  ctx.pane.getState().select([id]);
  void refreshLinkPreview(ctx.board, id);
  return id;
}

interface Preview {
  url: string;
  title: string;
  description: string;
  image: string | null;
  site: string;
  /** The picture itself as a data: URI, from sites whose image links expire (Instagram). */
  imageData?: string | null;
  imageName?: string;
}

/**
 * Fills a link card from the server's preview. A picture sent inline is kept on the board (and
 * uploaded like an import) rather than linked; a card still at the default size then takes the
 * picture's proportions, growing downwards.
 */
export async function refreshLinkPreview(board: BoardDoc, id: string): Promise<void> {
  const item = board.index.get(id);
  if (!item || item.type !== 'link') return;
  try {
    const p = await api.post<Preview>('/link-preview', { url: item.props.url, boardId: board.remote ? board.boardId : undefined });
    if (!board.index.has(id)) return;
    const kept = p.imageData ? await keepLinkImage(board, p.imageData, p.imageName || 'link-image') : null;
    const current = board.index.get(id);
    if (!current || current.type !== 'link') return;
    const patch: ItemPatch = {
      props: { title: p.title, description: p.description, image: kept ? null : p.image, site: p.site, status: 'ok', ...(kept ? { imageAssetId: kept.id } : {}) },
    };
    if (kept && current.w === DEFAULT_LINK_SIZE.w && current.h === DEFAULT_LINK_SIZE.h) {
      patch.h = linkCardHeight(current.w, kept.width, kept.height);
    }
    board.updateItem(id, patch);
  } catch {
    if (board.index.has(id)) board.updateItem(id, { props: { status: 'error' } });
  }
}

/** Stores a picture sent as a data: URI as an image asset of the board; null if it will not decode. */
async function keepLinkImage(board: BoardDoc, dataUri: string, name: string): Promise<LocalAssetMeta | null> {
  if (!dataUri.startsWith('data:image/')) return null;
  try {
    const blob = await (await fetch(dataUri)).blob();
    const file = new File([blob], `${name}.${blob.type.slice(6).replace('jpeg', 'jpg')}`, { type: blob.type });
    const maxSide = board.meta.get('importMaxSide');
    const meta = await storeImageAsset(file, board.boardId, { maxSide: typeof maxSide === 'number' ? maxSide : 0 });
    if (board.remote) void enqueueUpload(board.boardId, meta.id);
    return meta;
  } catch (e) {
    console.warn('[link] preview picture not kept', e);
    return null;
  }
}

/** Height of the text under a link card's picture with a two-line title (.link-body, 13 px UI font). */
const LINK_TEXT_H = 86;

/**
 * Card height at which a w-wide card shows a pw × ph picture whole, from 16:9 landscape to 3:4
 * portrait; taller pictures (reels) are cropped to 3:4, as in Instagram's grid.
 */
function linkCardHeight(w: number, pw: number, ph: number): number {
  const inner = w - 4; // the card's 2 px border
  const ratio = Math.min(4 / 3, Math.max(9 / 16, ph / Math.max(1, pw)));
  return Math.round(inner * ratio + LINK_TEXT_H + 4);
}

/** World point at the centre of the pane. */
export function viewCenter(pane: PaneStore): Point {
  const { camera, size } = pane.getState();
  return screenToWorld(camera, { x: size.w / 2, y: size.h / 2 });
}

// ---- clipboard --------------------------------------------------------------

// Storage lives in clipboard.ts (this tab, other tabs via localStorage, the system clipboard via
// the copy/cut events wired in useKeyboard). Repeated pastes of one copy at one spot step down
// and right.
let pasteCount = 0;
let pastedStamp = '';
let pastedAt: Point | null = null;

const hasRich = (items: Item[]) => items.some((i) => RICH_TYPES.has(i.type));

/**
 * Connectors among copies attach to the copies of their items (`ids` maps an original's id to
 * its copy's) and their loose points shift with them; a copy's input `x` against its original's
 * gives the shift.
 */
function relinkConnectors(inputs: NewItem[], originals: Item[], ids: Map<string, string>): void {
  inputs.forEach((input, i) => {
    const original = originals[i];
    if (input.type !== 'connector' || !original) return;
    const newIdOf = (id: string) => ids.get(id);
    input.props = copyConnectorProps(input.props, newIdOf, input.x - original.x, input.y - original.y);
  });
}

/**
 * Snapshot items as inputs for new ones, shifted by (dx, dy). Copies of grouped items form
 * groups of their own, connectors stay attached to the copies of their items; `layerOf` says
 * which layer a copy lands on (undefined: the target board's automatic layer for its type).
 */
export function asNewItems(items: Item[], dx: number, dy: number, layerOf: (item: Item) => string | null | undefined): NewItem[] {
  const groups = new Map<string, string>();
  const ids = new Map(items.map((item) => [item.id, newId()]));
  const inputs = items.map((item) => {
    const { id: _id, z: _z, createdAt: _c, updatedAt: _u, layerId: _l, ...rest } = item;
    void _id;
    void _z;
    void _c;
    void _u;
    void _l;
    let groupId: string | null = null;
    if (item.groupId) {
      groupId = groups.get(item.groupId) ?? newId();
      groups.set(item.groupId, groupId);
    }
    const layerId = layerOf(item);
    return { ...rest, x: item.x + dx, y: item.y + dy, groupId, ...(layerId === undefined ? {} : { layerId }), id: ids.get(item.id)! } as NewItem;
  });
  relinkConnectors(inputs, items, ids);
  return inputs;
}

/** Rewrites asset props (images, files, covers, link pictures, sheet layers) to fresh clones when items cross into another board (or the cutout drawer). */
export async function relinkAssets(items: NewItem[], fromBoard: string, to: BoardDoc): Promise<void> {
  if (fromBoard === to.boardId) return;
  for (const item of items) {
    if (item.type === 'boardcard') {
      // a custom cover belongs to the source board; without one the card shows the live preview
      if (!item.props.coverAssetId) continue;
      const newId = await cloneAssetLocally(item.props.coverAssetId);
      item.props = { ...item.props, coverAssetId: newId };
      if (newId && to.remote) void enqueueUpload(to.boardId, newId);
      continue;
    }
    if (item.type === 'link') {
      // a kept picture comes along; without one the card links to the site's image as before
      if (!item.props.imageAssetId) continue;
      const newId = await cloneAssetLocally(item.props.imageAssetId);
      item.props = { ...item.props, imageAssetId: newId };
      if (newId && to.remote) void enqueueUpload(to.boardId, newId);
      continue;
    }
    if (item.type === 'sheet') {
      // each layer's pixels come along; a layer whose asset cannot be fetched arrives empty
      const cloned = new Map<string, string | null>();
      const layers = { ...item.props.layers };
      for (const [lid, l] of Object.entries(layers)) {
        if (!l.assetId) continue;
        let copy = cloned.get(l.assetId);
        if (copy === undefined) {
          copy = await cloneAssetLocally(l.assetId);
          cloned.set(l.assetId, copy);
          if (copy && to.remote) void enqueueUpload(to.boardId, copy);
        }
        layers[lid] = { ...l, assetId: copy };
      }
      item.props = { ...item.props, layers };
      continue;
    }
    if (item.type !== 'image' && item.type !== 'file') continue;
    const newId = await cloneAssetLocally(item.props.assetId);
    if (!newId) continue;
    item.props = { ...item.props, assetId: newId } as typeof item.props;
    if (to.remote) void enqueueUpload(to.boardId, newId);
  }
}

/**
 * Copies the selection. The snapshot is taken at once, so the copy event that follows Ctrl+C
 * can fill the system clipboard synchronously; rich bodies whose module is not loaded yet are
 * snapshotted again under the same stamp once it is, and that fuller copy then wins on paste.
 */
export async function copySelection(ctx: Ctx): Promise<ClipboardPayload | null> {
  const items = selectedItems(ctx);
  if (!items.length) return null;
  const ids = items.map((i) => i.id);
  let payload = snapshotForClipboard(ctx.board, ids);
  rememberClipboard(payload);
  if (hasRich(items) && !richSync()) {
    await loadRich();
    if (ids.every((id) => ctx.board.index.has(id))) {
      payload = snapshotForClipboard(ctx.board, ids, payload.stamp);
      rememberClipboard(payload);
    }
  }
  return payload;
}

export async function cutSelection(ctx: Ctx): Promise<void> {
  await copySelection(ctx);
  deleteSelection(ctx);
}

/**
 * Pastes the given copy (from a paste event) or the newest one this browser holds, centred on
 * `at` (the mouse cursor). Without a point, copies from this board land 24 px down and right of
 * the originals and copies from another board centred in the view. Pasting the same copy again
 * at the same spot steps it another 24 px. Assets are cloned across boards; pasted items form
 * groups of their own, and keep their layer only where the target board has it.
 */
export async function pasteClipboard(ctx: Ctx, at?: Point, payload?: ClipboardPayload | null): Promise<void> {
  const clip = payload ?? readStoredClipboard();
  if (!clip || !clip.items.length) return;
  if (hasRich(clip.items)) await loadRich();
  const sameBoard = clip.boardId === ctx.board.boardId;
  if (!sameBoard && !at) at = viewCenter(ctx.pane);
  // a mouse nudged by a few screen pixels between pastes still counts as the same spot
  const zoom = ctx.pane.getState().camera.zoom;
  const sameSpot = at && pastedAt ? distance(at, pastedAt) * zoom < 4 : !at && !pastedAt;
  if (clip.stamp !== pastedStamp || !sameSpot) {
    pastedStamp = clip.stamp;
    pasteCount = 0;
  }
  pastedAt = at ?? null;
  pasteCount++;
  const bounds = unionRects(clip.items.map(rotatedBounds));
  const step = 24 * (at ? pasteCount - 1 : pasteCount);
  // one whole-unit shift for every item, so the copy keeps its layout exactly
  const dx = step + (at && bounds ? Math.round(at.x - bounds.x - bounds.w / 2) : 0);
  const dy = step + (at && bounds ? Math.round(at.y - bounds.y - bounds.h / 2) : 0);
  const inputs = asNewItems(clip.items, dx, dy, (item) => (sameBoard || (item.layerId && ctx.board.layer(item.layerId)) ? item.layerId : null));
  await relinkAssets(inputs, clip.boardId, ctx.board);
  const ids = ctx.board.createItems(inputs);
  ctx.pane.getState().select(ids);
}

/**
 * Creates copies of items on the same board, shifted by (dx, dy), in one transaction. Copies of
 * grouped items form groups of their own. Rich bodies are copied as they are when the rich
 * module is loaded (it is whenever such an item is on screen); call loadRich first otherwise.
 */
export function cloneItems(board: BoardDoc, items: Item[], dx: number, dy: number): string[] {
  const snapshot = board.snapshotItems(items.map((i) => i.id));
  const groups = new Map<string, string>();
  const ids = new Map(snapshot.map((item) => [item.id, newId()]));
  const inputs: NewItem[] = snapshot.map((item) => {
    const { id: _id, z: _z, createdAt: _c, updatedAt: _u, ...rest } = item;
    void _id;
    void _z;
    void _c;
    void _u;
    let groupId: string | null = null;
    if (item.groupId) {
      groupId = groups.get(item.groupId) ?? newId();
      groups.set(item.groupId, groupId);
    }
    return { ...rest, id: ids.get(item.id)!, groupId, x: item.x + dx, y: item.y + dy } as NewItem;
  });
  relinkConnectors(inputs, snapshot, ids);
  return board.createItems(inputs);
}

export async function duplicateSelection(ctx: Ctx): Promise<void> {
  const items = selectedItems(ctx);
  if (!items.length) return;
  if (hasRich(items)) await loadRich();
  ctx.pane.getState().select(cloneItems(ctx.board, items, 24, 24));
}

export function hasClipboard(): boolean {
  return hasStoredClipboard();
}

// ---- camera -----------------------------------------------------------------

export function zoomBy(pane: PaneStore, factor: number, screenPt?: Point): void {
  const { camera, size, setCamera } = pane.getState();
  const pt = screenPt ?? { x: size.w / 2, y: size.h / 2 };
  setCamera(zoomAt(camera, pt, camera.zoom * factor));
}

export function zoomTo(pane: PaneStore, zoom: number): void {
  const { camera, size, setCamera } = pane.getState();
  setCamera(zoomAt(camera, { x: size.w / 2, y: size.h / 2 }, zoom));
}

export function zoomToFit(ctx: Ctx): void {
  const all = ctx.board.index.all();
  const bounds = unionRects(all.map(rotatedBounds));
  const { camera, size, setCamera } = ctx.pane.getState();
  if (!bounds) {
    setCamera(centerOn({ x: 0, y: 0 }, size, 1, camera.rot));
    return;
  }
  setCamera(fitRect(bounds, size, 64, 4, camera.rot));
}

export function zoomToSelection(ctx: Ctx): void {
  const bounds = selectionBounds(ctx);
  if (!bounds) return zoomToFit(ctx);
  const { camera, size, setCamera } = ctx.pane.getState();
  setCamera(fitRect(bounds, size, 96, 4, camera.rot));
}

/** Back to an upright canvas at 100%, keeping what is at the centre of the viewport there. */
export function resetView(pane: PaneStore): void {
  const { camera, size, setCamera } = pane.getState();
  const center = screenToWorld(camera, { x: size.w / 2, y: size.h / 2 });
  setCamera(centerOn(center, size, 1, 0));
}

/** Turn the canvas upright about the centre of the viewport. */
export function resetRotation(pane: PaneStore): void {
  const { camera, size, setCamera } = pane.getState();
  setCamera(rotateAt(camera, { x: size.w / 2, y: size.h / 2 }, 0));
}

export function visibleRect(pane: PaneStore): Rect {
  const { camera, size } = pane.getState();
  return visibleWorldRect(camera, size);
}

export function cameraFor(pane: PaneStore): Camera {
  return pane.getState().camera;
}

// ---- cross-pane / cross-board ----------------------------------------------------

/** Copies items into another board at new positions (split view drag between different boards). */
export async function copyItemsToBoard(
  from: BoardDoc,
  to: BoardDoc,
  positions: Map<string, Point>,
): Promise<string[]> {
  const ids = [...positions.keys()];
  const source = ids.map((id) => from.index.get(id)).filter((i): i is Item => !!i);
  if (hasRich(source)) await loadRich();
  const snapshot = from.snapshotItems(ids);
  const copyIds = new Map(snapshot.map((item) => [item.id, newId()]));
  const inputs: NewItem[] = snapshot.map((item) => {
    const { id: _id, z: _z, createdAt: _c, updatedAt: _u, groupId: _g, layerId: _l, ...rest } = item;
    void _id;
    void _z;
    void _c;
    void _u;
    void _g;
    void _l;
    const p = positions.get(item.id) ?? { x: item.x, y: item.y };
    return { ...rest, id: copyIds.get(item.id)!, x: p.x, y: p.y } as NewItem;
  });
  relinkConnectors(inputs, snapshot, copyIds);
  await relinkAssets(inputs, from.boardId, to);
  return to.createItems(inputs);
}

// ---- dev helpers ------------------------------------------------------------

export function stressTest(ctx: Ctx, count: number): void {
  const center = viewCenter(ctx.pane);
  const spread = Math.sqrt(count) * 260;
  const inputs: NewItem[] = [];
  for (let i = 0; i < count; i++) {
    inputs.push({
      type: 'note',
      x: center.x + (Math.random() - 0.5) * spread,
      y: center.y + (Math.random() - 0.5) * spread,
      w: 160 + Math.random() * 120,
      h: 160 + Math.random() * 120,
      rotation: Math.round((Math.random() - 0.5) * 20),
      props: {
        text: `Note ${i + 1}`,
        color: NOTE_COLORS[i % NOTE_COLORS.length]!,
        fontSize: 20,
      },
    });
  }
  ctx.board.createItems(inputs);
}

// ---- export -----------------------------------------------------------------

/** Downloads the board's items as JSON (assets referenced by id; the files come with a later export). */
// Board backups (JSON, ZIP with images) live in export/backup.ts and run from the export panel.
